import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { caseStudiesForService, testimonialsForService } from "@/lib/service-proof";

export function ServiceProof({ slug, serviceName }: { slug: string; serviceName: string }) {
  const studies = useMemo(() => caseStudiesForService(slug), [slug]);
  const quotes = useMemo(() => testimonialsForService(slug), [slug]);

  const industryOptions = useMemo(() => {
    const set = new Set<string>();
    studies.forEach((s) => set.add(s.industry));
    quotes.forEach((q) => set.add(q.industry));
    return Array.from(set);
  }, [studies, quotes]);

  const [industry, setIndustry] = useState<string>("All industries");

  if (studies.length === 0 && quotes.length === 0) return null;

  const visibleStudies =
    industry === "All industries" ? studies : studies.filter((s) => s.industry === industry);
  const visibleQuotes =
    industry === "All industries" ? quotes : quotes.filter((q) => q.industry === industry);

  return (
    <section className="px-6 py-16" aria-labelledby="service-proof-heading">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <h2
            id="service-proof-heading"
            className="font-display text-3xl font-bold text-brand-navy md:text-4xl"
          >
            Results from {serviceName.toLowerCase()} engagements
          </h2>
          <p className="mt-4 max-w-2xl text-brand-navy/60">
            Filter by the industry closest to yours to see the work and the numbers behind it.
          </p>
        </Reveal>

        {industryOptions.length > 1 && (
          <Reveal>
            <div
              role="group"
              aria-label="Filter results by industry"
              className="mt-8 flex flex-wrap gap-2"
            >
              {["All industries", ...industryOptions].map((opt) => {
                const active = opt === industry;
                return (
                  <button
                    key={opt}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setIndustry(opt)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                      active
                        ? "border-brand-accent bg-brand-accent text-background"
                        : "border-brand-navy/10 text-brand-navy/65 hover:border-brand-accent/40 hover:text-brand-navy"
                    }`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </Reveal>
        )}

        {visibleStudies.length > 0 && (
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            {visibleStudies.map((s, i) => (
              <Reveal key={s.id} delay={(i % 2) * 0.07}>
                <article
                  id={`case-${s.id}`}
                  className="h-full scroll-mt-28 rounded-3xl border border-brand-navy/5 bg-brand-surface p-8"
                >
                  <p className="text-xs font-bold uppercase tracking-widest text-brand-accent">
                    {s.industry}
                  </p>
                  <h3 className="mt-3 font-display text-xl font-bold leading-snug text-brand-navy">
                    {s.headline}
                  </h3>
                  <p className="mt-2 text-sm font-medium text-brand-navy/50">{s.client}</p>
                  <dl className="mt-6 grid grid-cols-3 gap-4 rounded-2xl bg-background p-5">
                    {s.metrics.map((m) => (
                      <div key={m.label}>
                        <dt className="sr-only">{m.label}</dt>
                        <dd className="font-display text-lg font-extrabold leading-tight text-brand-navy">
                          {m.value}
                        </dd>
                        <p className="mt-1 text-[11px] leading-tight text-brand-navy/55">
                          {m.label}
                        </p>
                      </div>
                    ))}
                  </dl>
                  <div className="mt-6 space-y-4 text-sm leading-relaxed text-brand-navy/65">
                    <p>
                      <span className="font-semibold text-brand-navy">Challenge. </span>
                      {s.challenge}
                    </p>
                    <p>
                      <span className="font-semibold text-brand-navy">What we did. </span>
                      {s.approach}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}

        {visibleQuotes.length > 0 && (
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {visibleQuotes.map((q, i) => (
              <Reveal key={q.id} delay={(i % 3) * 0.06}>
                <figure className="h-full rounded-3xl border border-brand-navy/10 p-8">
                  <blockquote className="text-base leading-relaxed text-brand-navy/80">
                    “{q.quote}”
                  </blockquote>
                  <figcaption className="mt-6 border-t border-brand-navy/10 pt-4">
                    <p className="font-bold text-brand-navy">{q.author}</p>
                    <p className="mt-1 text-sm text-brand-navy/55">{q.role}</p>
                    <p className="mt-2 text-xs font-bold uppercase tracking-widest text-brand-accent">
                      {q.industry}
                    </p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        )}

        <Reveal>
          <div className="mt-10 flex flex-wrap items-center gap-4 rounded-3xl border border-brand-navy/10 bg-brand-surface p-6">
            <p className="text-sm text-brand-navy/65">
              Want results like these for your{" "}
              {industry === "All industries" ? "business" : industry} team?
            </p>
            <Link
              to="/contact"
              search={{
                service: serviceName,
                industry: industry === "All industries" ? undefined : industry,
              }}
              className="rounded-xl bg-brand-accent px-6 py-3 text-sm font-bold text-background transition-[background-color,box-shadow,transform] duration-150 ease-out hover:-translate-y-0.5 hover:shadow-lg hover:shadow-brand-accent/25 active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none"
            >
              {industry === "All industries"
                ? `Discuss ${serviceName.toLowerCase()}`
                : `Discuss ${serviceName.toLowerCase()} for ${industry}`}
            </Link>
          </div>
        </Reveal>

        {visibleStudies.length === 0 && visibleQuotes.length === 0 && (
          <p className="mt-10 text-brand-navy/60">
            No published results for that industry yet — ask us and we'll share a comparable
            engagement.
          </p>
        )}
      </div>
    </section>
  );
}
